import axios from "axios";
import { REFRESH_URL, refreshToken } from "../../app/crud/auth.crud";
import { actions } from "../../app/store/ducks/auth.duck";

export function removeCSSClass(ele, cls) {
  const reg = new RegExp("(\\s|^)" + cls + "(\\s|$)");
  ele.className = ele.className.replace(reg, " ");
}

export function addCSSClass(ele, cls) {
  ele.classList.add(cls);
}

export const getImg = (url) => {
  return axios
    .get(url, { responseType: "arraybuffer" })
    .then((res) => {
      return new Buffer(res.data, "binary").toString("base64");
    })
    .then((res) => "data:image/jpeg;base64," + res)
    .catch((err) => {
      return err;
    });
};

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const toAbsoluteUrl = (pathname) => process.env.PUBLIC_URL + pathname;

const AUTH_PATHS = [
  "/auth/login",
  "/auth/sign-up",
  "/auth/signup-subscribe",
  "/auth/create-trial-account",
  "/auth/forgot-password",
];

function isAuthPath() {
  return (
    AUTH_PATHS.includes(window.location.pathname) ||
    window.location.pathname.includes("refresh-password") ||
    window.location.pathname.includes("accept-invitation")
  );
}

let isRefreshing = false;
let pendingRequests = [];

function processQueue(error, token = null) {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingRequests = [];
}

export function setupAxios(axios, store) {
  axios.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken },
      } = store.getState();

      if (process.env.REACT_APP_WITH_CREDENTIALS) {
        config.withCredentials = true;
        config.headers["Access-Control-Allow-Origin"] =
          process.env.REACT_APP_WITH_CREDENTIALS;
        config.headers["Access-Control-Allow-Credentials"] = true;
      }
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
        config.headers.local = timezone;
      }

      return config;
    },
    (err) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !isAuthPath() &&
        !originalRequest._retry &&
        originalRequest.url !== REFRESH_URL
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingRequests.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          refreshToken()
            .then((response) => {
              const newToken = response.data.data.accessToken;
              store.dispatch(actions.updateToken(newToken));
              axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              processQueue(null, newToken);
              resolve(axios(originalRequest));
            })
            .catch((refreshError) => {
              processQueue(refreshError, null);
              localStorage.clear();
              window.location.pathname = "/auth/login";
              reject(refreshError);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      return Promise.reject(error);
    }
  );
}

/*  removeStorage: removes a key from localStorage and its sibling expiracy key
    params:
        key <string>     : localStorage key to remove
    returns:
        <boolean> : telling if operation succeeded
 */

export function removeStorage(key) {
  try {
    localStorage.setItem(key, "");
    localStorage.setItem(key + "_expiresIn", "");
  } catch (e) {
    // console.log(
    //   "removeStorage: Error removing key [" +
    //   key +
    //   "] from localStorage: " +
    //   JSON.stringify(e)
    // );
    return false;
  }
  return true;
}

/*  getStorage: retrieves a key from localStorage previously set with setStorage().
    params:
        key <string> : localStorage key
    returns:
        <string> : value of localStorage key
        null : in case of expired key or failure
 */
export function getStorage(key) {
  const now = Date.now(); //epoch time, lets deal only with integer
  // set expiration for storage
  let expiresIn = localStorage.getItem(key + "_expiresIn");
  if (expiresIn === undefined || expiresIn === null) {
    expiresIn = 0;
  }

  expiresIn = Math.abs(expiresIn);
  if (expiresIn < now) {
    // Expired
    removeStorage(key);
    return null;
  } else {
    try {
      const value = localStorage.getItem(key);
      return value;
    } catch (e) {
      // console.log(
      //   "getStorage: Error reading key [" +
      //   key +
      //   "] from localStorage: " +
      //   JSON.stringify(e)
      // );
      return null;
    }
  }
}
/*  setStorage: writes a key into localStorage setting a expire time
    params:
        key <string>     : localStorage key
        value <string>   : localStorage value
        expires <number> : number of seconds from now to expire the key
    returns:
        <boolean> : telling if operation succeeded
 */
export function setStorage(key, value, expires) {
  if (expires === undefined || expires === null) {
    expires = 24 * 60 * 60; // default: seconds for 1 day
  }

  const now = Date.now(); //millisecs since epoch time, lets deal only with integer
  const schedule = now + expires * 1000;
  try {
    localStorage.setItem(key, value);
    localStorage.setItem(key + "_expiresIn", schedule);
  } catch (e) {
    // console.log(
    //   "setStorage: Error setting key [" +
    //   key +
    //   "] in localStorage: " +
    //   JSON.stringify(e)
    // );
    return false;
  }
  return true;
}
